import Hero from '@referralexchange/rea-ui-react/src/components/blocks/Hero';
import Image from '@referralexchange/rea-ui-react/src/atoms/Image';
import Content from '@referralexchange/rea-ui-react/src/atoms/Content';
import Heading from '@referralexchange/rea-ui-react/src/atoms/Heading';
import Section from '@referralexchange/rea-ui-react/src/atoms/Section';
import SidebarContainer from '@referralexchange/rea-ui-react/src/components/blocks/SidebarContainer';
import NumberedHeadingContent from '@referralexchange/rea-ui-react/src/components/content/NumberedHeadingContent';
import ComparisonList from '@referralexchange/rea-ui-react/src/components/ComparisonList';
import styles from '../components/InfoPages/InfoPage.module.scss';

import Head from '~/components/Meta/Head';
import Anchors from '@referralexchange/rea-ui-react/src/components/navigation/Anchors';
import Link from 'next/link';

import MessageAgentCta from '~/components/blocks/MessageAgentCta';
import { RENT_OR_BUY_A_HOME_FUNNEL } from '~/config/funnel';
import { useState } from 'react';

export default function Page({}) {
  const sidebar = (
    <Anchors
      items={[
        {
          name: (
            <MessageAgentCta
              standalone
              funnel={RENT_OR_BUY_A_HOME_FUNNEL}
              fullInteractive
            />
          ),
          href: null,
          hideOnMobile: true,
        },
        {
          name: 'Buying a Home',
          href: '#buying-a-home',
        },
        {
          name: 'Renting a Home',
          href: '#renting-a-home',
        },
      ]}
    />
  );

  const buyingAdvantagesComparisonList = {
    title: 'Advantages',
    list: [
      'Investment Potential',
      'Stability and Control',
      'Sense of Ownership',
      'Tax Benefits',
    ],
    symbol: '+',
  };

  const buyingDisadvantagesComparisonList = {
    title: 'Disadvantages',
    list: [
      'Financial Commitment',
      'Maintenance and Repairs',
      'Lack of Flexibility',
    ],
    symbol: '\u2013',
  };

  const rentingAdvantagesComparisonList = {
    title: 'Advantages',
    list: [
      'Flexibility',
      'Lower Upfront Costs',
      'Minimal Maintenance Responsibilities',
    ],
    symbol: '+',
  };

  const rentingDisadvantagesComparisonList = {
    title: 'Disadvantages',
    list: ['Lack of Equity', 'Rent Increases', 'Limited Control and Stability'],
    symbol: '\u2013',
  };

  const buyingAdvantagesList = [
    {
      heading: 'Investment Potential',
      content: (
        <p>
          One of the most significant advantages of buying a home is its
          potential to appreciate in value over time. Historically, real estate
          has been a reliable long-term investment, and homeownership allows you
          to build equity in your property.{' '}
          <Link href={'/how-to-find-homes-for-sale-near-you'}>
            Finding homes for sale near you
          </Link>{' '}
          and reviewing the home value’s growth is a great way to see how homes
          in your area perform.
        </p>
      ),
    },
    {
      heading: 'Stability and Control',
      content: (
        <p>
          When you buy a home, you have more control over your living space. You
          can make improvements or renovations according to your preferences
          without seeking permission from a landlord. Additionally,
          homeownership provides stability, knowing that you have a permanent
          place to live as long as you continue to make mortgage payments.
        </p>
      ),
    },
    {
      heading: 'Sense of Ownership',
      content: (
        <p>
          Owning a home can instill a sense of pride and accomplishment. It
          gives you the freedom to personalize your space and create a home that
          reflects your personality and lifestyle, compared to the limits you
          face when renting a home or apartment. For some it represents a
          tangible manifestation of one’s achievements and aspirations, serving
          as a symbol of stability, security, and success.
        </p>
      ),
    },
    {
      heading: 'Tax Benefits',
      content: (
        <p>
          Homeownership offers several tax advantages, including deductions for
          mortgage interest, property taxes, and certain closing costs. These
          deductions can help reduce your taxable income and potentially lower
          your overall tax bill, which are a silver lining considering home
          affordability is a barrier to purchasing for many.
        </p>
      ),
    },
  ];

  const buyingDisadvantagesList = [
    {
      heading: 'Financial Commitment',
      content: (
        <p>
          Buying a home requires a substantial financial commitment upfront,
          including a down payment, closing costs, and ongoing mortgage
          payments. These financial commitments can strain individual budgets
          and limit flexibility, particularly for those with limited savings.
          Additionally there is the opportunity cost associated with allocating
          these financial resources to purchase a home versus alternative
          available investment or business opportunities.
        </p>
      ),
    },
    {
      heading: 'Maintenance and Repairs',
      content: (
        <p>
          Once you’re a homeowner, you're responsible for maintaining your
          property and covering the costs of repairs and maintenance. This
          includes regular upkeep such as landscaping, repairs to appliances,
          and unexpected expenses like roof repairs or plumbing issues.
          Maintenance costs can accumulate over time, adding to the overall
          expenses associated with homeownership whether you buy a single family
          home, townhouse, or condo.
        </p>
      ),
    },
    {
      heading: 'Lack of Flexibility',
      content: (
        <p>
          Buying a home ties you down to a specific location, which can limit
          your flexibility, especially if you need to relocate for work or
          personal reasons. Selling a home can also be time-consuming and
          costly, particularly in a slow real estate market. Changes in
          employment, family dynamics, or personal preferences may necessitate
          relocation, posing challenges for homeowners.
        </p>
      ),
    },
  ];

  const rentingAdvantagesList = [
    {
      heading: 'Flexibility',
      content: (
        <p>
          Renting a home offers greater flexibility compared to buying. You have
          the freedom to move more easily without the hassle of selling a
          property, which can be beneficial if you're unsure about your
          long-term plans, experimenting with new neighborhoods or need to
          relocate frequently. Renting allows individuals to adapt to changing
          circumstances and explore different living arrangements without
          long-term commitments. And, given the lower stakes, it’s typically
          much easier to find a house available to rent over a home you want to
          buy.
        </p>
      ),
    },
    {
      heading: 'Lower Upfront Costs',
      content: (
        <p>
          Renting a property requires less upfront capital than buying a home.
          You'll typically only need a security deposit and possibly the first
          month's rent, making it more accessible for individuals with limited
          savings or uncertain financial situations. This lower barrier to entry
          makes renting more accessible to individuals with limited financial
          resources or credit histories. Additionally, it’s generally more
          affordable to rent from a monthly perspective than buy as certain
          costs (such as taxes, HOA fees, and some utilities) are paid by the
          owner.
        </p>
      ),
    },
    {
      heading: 'Minimal Maintenance Responsibilities',
      content: (
        <p>
          One of the advantages of finding a home to rent is that you're not
          responsible for maintenance and repairs. Landlords are generally
          responsible for property upkeep, including repairs and maintenance.
          Tenants can enjoy the benefits of the property, without shouldering
          the associated burdens, allowing for a more hassle-free living
          experience.
        </p>
      ),
    },
  ];

  const rentingDisadvantagesList = [
    {
      heading: 'Lack of Equity',
      content: (
        <p>
          Unlike homeownership, renting does not allow you to build equity in a
          property. You're essentially paying for temporary occupancy without
          any long-term financial benefits or asset appreciation. Individuals
          who rent may miss out on the wealth-building opportunity associated
          with homeownership.
        </p>
      ),
    },
    {
      heading: 'Rent Increases',
      content: (
        <p>
          Rent prices can fluctuate over time, and landlords may increase rent
          at the end of your lease term or with minimal notice. While homeowners
          benefit from fixed-rate mortgages and predictable housing expenses,
          renters may face uncertainty regarding future rental costs given the
          swift rental market changes that can occur. These changes can impact
          your budget and make it challenging to predict future housing
          expenses. Further, a change in rent is not equal across property
          types, and could be more or less depending on if you rent a townhome,
          condo, apartment, or single family home.
        </p>
      ),
    },
    {
      heading: 'Limited Control and Stability',
      content: (
        <>
          <p>
            Renting from someone else means you're subject to the rules and
            regulations set by your landlord or property management company,
            limiting your autonomy and control over their living environments.
            Additionally, rental agreements are typically short-term, offering
            less stability and security compared to long-term homeownership.
          </p>
          <p>
            Ultimately, the decision to buy or rent a home depends on your
            individual circumstances, preferences, priorities, and financial
            goals. Both options offer distinct advantages and disadvantages, and
            it's essential to consider factors such as your long-term plans,
            financial stability, and lifestyle before making a decision.
          </p>
          <p>
            Whether you choose to rent or buy, the journey towards home
            ownership or rental living is a deeply personal and transformative
            experience and{' '}
            <Link href={'/how-to-find-a-real-estate-agent'}>
              finding a top real estate agent
            </Link>{' '}
            can provide valuable insights and guidance tailored to your specific
            needs.
          </p>
        </>
      ),
    },
  ];
  return (
    <>
      <Head
        url={`${process.env.CLIENT_ORIGIN}/should-you-rent-or-buy-a-home`}
        title={'Should You Rent or Buy a Home | RealEstateAgents.com'}
        description={
          'Use this guide to learn the steps you can take when comparing real estate agents.'
        }
      />
      <Hero
        heading={'Should You Rent or Buy a Home?'}
        content={
          "It's hard to know whether buying or renting is the right choice. We've put together this resource to help guide your decision making and home buying journey!"
        }
        image={
          <Image
            src="/static/images/should-you-rent-or-buy-a-home/hero.png"
            priority
            alt={'Should You Rent or Buy a Home'}
          />
        }
      />
      <SidebarContainer
        sidebar={sidebar}
        mobileMenuProps={{
          top: true,
          defaultTitle: 'Should You Rent or Buy a Home?',
        }}
        mobileMenuEnabled={true}
      >
        <Content>
          <p>
            Deciding whether to buy a home or rent is a significant decision
            that largely depends on various personal factors, including your
            financial situation, lifestyle, priorities and long-term goals. Both
            options come with their own set of advantages and disadvantages, and
            it’s essential to carefully weigh them before making a decision.
          </p>
          <Section>
            <Heading tag="h2" type="h1" anchor="Buying a Home">
              Buying a Home
            </Heading>
            <ComparisonList
              listOne={buyingAdvantagesComparisonList}
              listTwo={buyingDisadvantagesComparisonList}
            />
            <Heading tag="h3" type="h3">
              Advantages of Buying a Home
            </Heading>
            <NumberedHeadingContent items={buyingAdvantagesList} />
            <Heading tag="h3" type="h3">
              Disadvantages of Buying a Home
            </Heading>
            <NumberedHeadingContent items={buyingDisadvantagesList} />
          </Section>
          <MessageAgentCta standalone funnel={RENT_OR_BUY_A_HOME_FUNNEL} />
          <Section>
            <Heading tag="h2" type="h1" anchor="Renting a Home">
              Renting a Home
            </Heading>
            <ComparisonList
              listOne={rentingAdvantagesComparisonList}
              listTwo={rentingDisadvantagesComparisonList}
            />
            <Heading tag="h3" type="h3" anchor="Past & current performance">
              Advantage of Renting a Home
            </Heading>
            <NumberedHeadingContent items={rentingAdvantagesList} />
            <Heading tag="h3" type="h3" anchor="Past & current performance">
              Disadvantages of Renting a Home
            </Heading>
            <NumberedHeadingContent items={rentingDisadvantagesList} />
          </Section>
          <MessageAgentCta standalone funnel={RENT_OR_BUY_A_HOME_FUNNEL} />
        </Content>
      </SidebarContainer>
      <div className={styles['InfoPage__mobile-cta-sticky']}>
        <MessageAgentCta
          standalone
          funnel={RENT_OR_BUY_A_HOME_FUNNEL}
          fullInteractive
        />
      </div>
    </>
  );
}
